import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild
} from '@angular/core';
import * as ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import { AiAssistantService } from './ai-assistant.service';

interface ChatMessage {
  type: 'user' | 'ai';
  text: string;
  data?: any[];
  allowDownload?: boolean;
  sourceQuery?: string;
  responseId?: number;
  feedback?: 'good' | 'bad' | null;

  page?: number;
  pageSize?: number;
}

@Component({
  selector: 'cs-ai-assistant',
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.scss'],
  providers: [AiAssistantService]
})
export class AiAssistantComponent implements AfterViewChecked {
  query: string = '';
  messages: ChatMessage[] = [];
  loading = false;
  isMinimised = false;

  quickPrompts = [
    'Leads created today',
    'Campaign performance'
  ];

  @Output()
  closePanel = new EventEmitter<void>();

  @ViewChild('chatContainer')
  private chatContainer!: ElementRef;

  constructor(private aiAssistantService: AiAssistantService) {}

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  usePrompt(prompt: string): void {
    this.query = prompt;
  }

  sendQuery(): void {
    const userQuery = this.query?.trim();

    if (!userQuery || this.loading) return;

    this.runQuery(userQuery);
  }

  private runQuery(userQuery: string): void {
    if (!userQuery || this.loading) return;

    this.messages.push({
      type: 'user',
      text: userQuery
    });

    const downloadFormat = this.getDownloadFormat(userQuery);

    this.query = '';
    this.loading = true;

    this.aiAssistantService.askQuery(userQuery).subscribe({
      next: res => {
        if (res?.data?.length) {
          const aiResponse = `Found ${res.data.length} records`;

          this.messages.push({
            type: 'ai',
            text: aiResponse,
            data: res.data,
            allowDownload: true,
            sourceQuery: userQuery,
            responseId: Number(res.responseId),
            feedback: null,
            page: 1,
            pageSize: 10
          });

          if (downloadFormat) {
            this.downloadData(res.data, downloadFormat);
          }
        } else {
          this.messages.push({
            type: 'ai',
            text: 'No results found',
            sourceQuery: userQuery,
            feedback: null
          });
        }

        this.loading = false;
      },

      error: () => {
        this.messages.push({
          type: 'ai',
          text: 'Something went wrong while processing the query.',
          sourceQuery: userQuery,
          feedback: null
        });

        this.loading = false;
      }
    });
  }

  setMessageFeedback(
    message: ChatMessage,
    feedback: 'good' | 'bad'
  ): void {
    const nextFeedback = message.feedback === feedback ? null : feedback;
    message.feedback = nextFeedback;

    if (!message.responseId || Number.isNaN(message.responseId) || nextFeedback === null) return;

    this.aiAssistantService
      .submitFeedback({
        responseId: message.responseId,
        feedback: nextFeedback === 'good'
      })
      .subscribe();
  }

  retryQuery(query: string | undefined): void {
    const prompt = query?.trim();

    if (!prompt || this.loading) return;

    this.query = prompt;
    this.runQuery(prompt);
  }

  /* ----------------------------------------
     Detect download format
  ---------------------------------------- */

  private getDownloadFormat(query: string): 'csv' | 'excel' | 'pdf' | null {
    const q = query.toLowerCase();

    if (q.includes('excel') || q.includes('xlsx')) return 'excel';

    if (q.includes('pdf')) return 'pdf';

    if (q.includes('csv')) return 'csv';

    if (q.includes('download') || q.includes('export')) return 'csv';

    return null;
  }

  /* ----------------------------------------
     Sorting
  ---------------------------------------- */

  sortData(message: ChatMessage, column: string) {
    if (!message.data) return;

    message.data = [...message.data].sort((a, b) => {
      if (a[column] > b[column]) return 1;
      if (a[column] < b[column]) return -1;

      return 0;
    });
  }

  /* ----------------------------------------
     Pagination
  ---------------------------------------- */

  getPagedData(message: ChatMessage): any[] {
    if (!message.data) return [];

    const page = message.page ?? 1;
    const pageSize = message.pageSize ?? 10;

    const start = (page - 1) * pageSize;

    return message.data.slice(start, start + pageSize);
  }

  getTotalPages(message: ChatMessage): number {
    if (!message.data?.length) return 1;

    return Math.ceil(message.data.length / (message.pageSize ?? 10));
  }

  nextPage(message: ChatMessage) {
    if (!message.data) return;

    const totalPages = this.getTotalPages(message);

    if ((message.page ?? 1) < totalPages) {
      message.page = (message.page ?? 1) + 1;
    }
  }

  prevPage(message: ChatMessage) {
    if ((message.page ?? 1) > 1) {
      message.page = (message.page ?? 1) - 1;
    }
  }

  /* ----------------------------------------
     Download CSV / Excel
  ---------------------------------------- */

  downloadData(data: any[], format: 'csv' | 'excel' | 'pdf' = 'csv'): void {
    if (!data || !data.length) return;

    const headers = Object.keys(data[0]);

    if (format === 'pdf') {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const leftMargin = 28;
      const topMargin = 34;
      const bottomMargin = 28;
      const rowHeight = 18;
      const headerHeight = 22;

      const usableWidth = pageWidth - leftMargin * 2;
      const colWidth = usableWidth / headers.length;

      let y = topMargin;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('AI Assistant Results', leftMargin, y);
      y += 16;

      const drawHeader = () => {
        doc.setFillColor(245, 245, 245);
        doc.rect(leftMargin, y, usableWidth, headerHeight, 'F');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);

        headers.forEach((h, i) => {
          const x = leftMargin + i * colWidth + 4;
          const text = this.truncatePdfText(
            doc,
            this.formatHeader(h),
            colWidth - 8
          );
          doc.text(text, x, y + 14);
        });

        y += headerHeight;
      };

      drawHeader();

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);

      data.forEach((row, rowIndex) => {
        if (y + rowHeight > pageHeight - bottomMargin) {
          doc.addPage();
          y = topMargin;
          drawHeader();
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
        }

        headers.forEach((h, i) => {
          const x = leftMargin + i * colWidth + 4;
          const raw = this.formatValue(row[h]);
          const text = this.truncatePdfText(doc, raw, colWidth - 8);
          doc.text(text, x, y + 12);
        });

        doc.setDrawColor(236, 236, 236);
        doc.line(
          leftMargin,
          y + rowHeight,
          leftMargin + usableWidth,
          y + rowHeight
        );

        y += rowHeight;
      });

      doc.save(`ai-results-${Date.now()}.pdf`);
    } else if (format === 'excel') {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Results');

      // Add header row
      const headerRow = headers.map(h => this.formatHeader(h));
      worksheet.addRow(headerRow);

      // Style header
      worksheet.getRow(1).font = { bold: true };

      // Add data rows
      data.forEach(row => {
        const values = headers.map(h => {
          const value = row[h];

          if (this.isDate(value)) {
            const parts = value.split('-'); // dd-mm-yyyy
            return new Date(+parts[2], +parts[1] - 1, +parts[0]);
          }

          return value;
        });

        const newRow = worksheet.addRow(values);

        values.forEach((v, i) => {
          if (this.isDate(row[headers[i]])) {
            newRow.getCell(i + 1).numFmt = 'dd-mm-yyyy';
          }
        });
      });

      // Auto column width
      worksheet.columns.forEach((column, i) => {
        let maxLength = 10;

        worksheet.eachRow({ includeEmpty: true }, row => {
          const cell = row.getCell(i + 1);

          const value = cell.value ? cell.value.toString() : '';

          maxLength = Math.max(maxLength, value.length);
        });

        column.width = maxLength + 2;
      });

      workbook.xlsx.writeBuffer().then(buffer => {
        const blob = new Blob([buffer], {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-results-${Date.now()}.xlsx`;
        a.click();

        window.URL.revokeObjectURL(url);
      });
    } else {
      // CSV export
      const headerRow = headers.map(h => this.formatHeader(h)).join(',');

      const rows = data.map(row =>
        headers.map(h => `"${this.formatValue(row[h])}"`).join(',')
      );

      const csvContent = [headerRow, ...rows].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-results-${Date.now()}.csv`;
      a.click();

      window.URL.revokeObjectURL(url);
    }
  }

  private truncatePdfText(doc: jsPDF, value: string, maxWidth: number): string {
    const source = value || '';

    if (doc.getTextWidth(source) <= maxWidth) return source;

    let text = source;

    while (text.length > 0 && doc.getTextWidth(`${text}...`) > maxWidth) {
      text = text.slice(0, -1);
    }

    return text ? `${text}...` : '...';
  }

  private isDate(value: any): boolean {
    if (!value) return false;

    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;

    return dateRegex.test(value.toString());
  }

  private formatHeader(header: string): string {
    return header
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  /* ----------------------------------------
     Scroll Chat
  ---------------------------------------- */

  private scrollToBottom(): void {
    try {
      this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
    } catch (err) {}
  }

  close(): void {
    this.closePanel.emit();
  }

  minimise(): void {
    this.isMinimised = !this.isMinimised;
  }

  private formatValue(value: any): string {
    if (!value) return '';

    if (value instanceof Date) {
      const d = value.getDate().toString().padStart(2, '0');
      const m = (value.getMonth() + 1).toString().padStart(2, '0');
      const y = value.getFullYear();

      return `${d}-${m}-${y}`;
    }

    return value.toString().replace(/\r|\n/g, '').trim();
  }
}
